import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
    GroupedTableHeader,
    type GroupedHeaderCell,
} from '@/components/ui/data-table-customized'

interface OverviewTableHeaderProps {
    months: string[]
    mergeAllocated: boolean
}

/** 3-level grouped header for revenue overview table */
export function OverviewTableHeader({
    months,
    mergeAllocated,
}: OverviewTableHeaderProps) {
    const { t } = useTranslation('report')

    const monthCols = mergeAllocated ? 2 : 3
    const typeLabels = ['renewal', 'new_sale', 'upsell'] as const

    // Helper to build type-group cells
    const typeGroupCells = (
        prefix: string,
        bg: string,
        text: string,
        rowSpan?: number
    ): GroupedHeaderCell[] =>
        typeLabels.map((type) => ({
            key: `${prefix}-${type}`,
            label: t(`payment_type.${type}`),
            className: `min-w-[90px] ${bg} ${text}`,
            ...(rowSpan ? { rowSpan } : {}),
        }))

    // --- Row 1: Top-level groups ---
    const row1: GroupedHeaderCell[] = [
        {
            key: 'stt',
            label: 'STT',
            rowSpan: 3,
            className: 'bg-slate-100 min-w-[50px]',
            sticky: { left: '0px' },
        },
        {
            key: 'service',
            label: t('overview.service'),
            rowSpan: 3,
            className: 'bg-slate-100 w-[350px]',
            align: 'left',
            sticky: { left: '50px' },
        },
        {
            key: 'monthly',
            label: t('overview.monthly_revenue'),
            colSpan: months.length * monthCols,
            className: 'bg-blue-100 text-blue-800',
        },
        {
            key: 'allocated',
            label: t('overview.allocated_by_type'),
            colSpan: 3,
            className: 'bg-green-100 text-green-800',
        },
        {
            key: 'unallocated',
            label: t('overview.unallocated_by_type'),
            colSpan: 3,
            className: 'bg-amber-100 text-amber-800',
        },
        {
            key: 'total-type',
            label: t('overview.total_by_type'),
            colSpan: 3,
            className: 'bg-indigo-100 text-indigo-800',
        },
        {
            key: 'total-rev',
            label: t('overview.total_revenue'),
            rowSpan: 3,
            className: 'bg-emerald-100 text-emerald-800',
            minWidth: '150px',
        },
        {
            key: 'pct',
            label: t('overview.percentage'),
            rowSpan: 3,
            className: 'bg-emerald-100 text-emerald-800',
            minWidth: '80px',
        },
    ]

    // --- Row 2: Month names + type sub-headers (rowSpan 2) ---
    const row2: GroupedHeaderCell[] = [
        // Month names
        ...months.map((month) => ({
            key: `m-${month}`,
            label: dayjs(month).format('MM/YYYY'),
            colSpan: monthCols,
            className: 'bg-blue-50 text-blue-700',
        })),
        // Type sub-headers with rowSpan 2 for each group
        ...typeGroupCells('alloc', 'bg-green-50', 'text-green-700', 2),
        ...typeGroupCells('unalloc', 'bg-amber-50', 'text-amber-700', 2),
        ...typeGroupCells('summary', 'bg-indigo-50', 'text-indigo-700', 2),
    ]

    // --- Row 3: PB prev / PB curr / KPB per month ---
    const row3: GroupedHeaderCell[] = months.flatMap((month) => {
        const cells: GroupedHeaderCell[] = []
        if (mergeAllocated) {
            cells.push({
                key: `${month}-merged`,
                label: t('overview.pb_merged'),
                className: 'bg-blue-50/70 text-blue-600',
                minWidth: '150px',
            })
        } else {
            cells.push(
                {
                    key: `${month}-prev`,
                    label: t('overview.pb_prev_short'),
                    className: 'bg-purple-50 text-purple-600',
                    minWidth: '150px',
                },
                {
                    key: `${month}-curr`,
                    label: t('overview.pb_curr_short'),
                    className: 'bg-blue-50/70 text-blue-600',
                    minWidth: '150px',
                }
            )
        }
        cells.push({
            key: `${month}-kpb`,
            label: 'KPB',
            className: 'bg-orange-50 text-orange-600',
            minWidth: '150px',
        })
        return cells
    })

    return <GroupedTableHeader stickyTop rows={[row1, row2, row3]} />
}
